/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api, getTenant } from "../../lib/auth";
import { toast } from "sonner";
import { Wallet, TrendingUp, Award, Copy, DollarSign, Building2, Loader2, ExternalLink, FileText, Users, BarChart3, Bell } from "lucide-react";
import { LeadsView } from "./realestate/LeadsView";
import { DailyReportsView } from "./realestate/DailyReportsView";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function MarketerPortalPage() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [storeUrl, setStoreUrl] = useState("");
  const [notifs, setNotifs] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/t/re/me");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("You are not registered as a marketer");
      else toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  useEffect(() => {
    load();
    const t = getTenant();
    if (t?.subdomain) setStoreUrl(`${window.location.origin}/store/${t.subdomain}`);
    api.get("/t/re/notifications?unread=true").then((r) => setNotifs(r.data || [])).catch(() => {});
  }, []);

  if (!data) return  
  const { marketer, sales, commissions, payouts } = data;

  const refLink = storeUrl ? `${storeUrl}?ref=${marketer.referral_code}` : "";

  const requestPayout = async () => {
    const amount = window.prompt(`Available wallet: ${ngn(marketer.wallet_balance)}\n\nHow much would you like to withdraw?`, String(marketer.wallet_balance));
    if (!amount || isNaN(parseFloat(amount))) return;
    const bank_name = window.prompt("Bank name?");
    if (!bank_name) return;
    const account_name = window.prompt("Account name?");
    if (!account_name) return;
    const account_number = window.prompt("Account number?");
    if (!account_number) return;
    setBusy(true);
    try {
      await api.post("/t/re/me/payout", { amount: parseFloat(amount), bank_name, account_name, account_number });
      toast.success("Payout requested — pending admin approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="marketer-portal">
      {/* Hero — Glassmorphism enterprise card */}
      <div className="relative overflow-hidden rounded-3xl mb-6 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-7">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-xl border border-white/20 flex items-center justify-center font-display font-extrabold text-3xl">
                {(marketer.name || "M")[0].toUpperCase()}
              </div>
              <div>
                <div className="text-xs uppercase font-bold tracking-[0.18em] text-white/70">Welcome back</div>
                <h1 className="font-display font-extrabold text-3xl mt-1">{marketer.name}</h1>
                <div className="text-sm text-white/70 mt-1 flex items-center gap-2 flex-wrap">
                  <span>Marketer</span>
                  <span>·</span>
                  <code className="font-mono bg-white/15 backdrop-blur px-2 py-0.5 rounded text-xs" data-testid="marketer-ref-code">{marketer.referral_code}</code>
                  <button onClick={() => { navigator.clipboard.writeText(marketer.referral_code); toast.success("Copied"); }} className="p-1 rounded hover:bg-white/15"><Copy size={11} /></button>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase font-bold tracking-[0.18em] text-white/70">Wallet balance</div>
              <div className="font-display font-extrabold text-5xl mt-1 tabular-nums" data-testid="marketer-wallet">{ngn(marketer.wallet_balance)}</div>
              <button onClick={requestPayout} disabled={busy || marketer.wallet_balance <= 0} data-testid="marketer-payout-btn"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-blue-900 font-bold text-sm disabled:opacity-50 hover:bg-white/95">
                <DollarSign size={13} /> Withdraw
              </button>
            </div>
          </div>
          {refLink && (
            <div className="mt-5 pt-5 border-t border-white/15 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-white/70">Your shareable storefront link</div>
              <div className="flex items-center gap-2">
                <code className="font-mono text-xs bg-white/10 backdrop-blur px-3 py-1.5 rounded-lg truncate max-w-md">{refLink}</code>
                <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Share link copied"); }} data-testid="marketer-share-link"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-bold">
                  <ExternalLink size={11} /> Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notifications strip */}
      {notifs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-3 mb-6 flex items-center gap-3" data-testid="marketer-notifs">
          <Bell size={15} />
          <div className="text-sm font-bold">{notifs.length} unread notification{notifs.length === 1 ? "" : "s"}</div>
          <div className="text-xs text-blue-700/70 truncate flex-1">Latest: {notifs[0].title}</div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-black/8 overflow-x-auto">
        {[
          { k: "overview", lbl: "Overview", icon: BarChart3 },
          { k: "leads", lbl: "Leads & CRM", icon: Users },
          { k: "reports", lbl: "Daily Report", icon: FileText },
          { k: "wallet", lbl: "Wallet & Commissions", icon: Wallet },
        ].map((t) => {
          const Icon = t.icon, active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`mp-tab-${t.k}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap ${active ? "border-blue-600 text-blue-700" : "border-transparent text-black/55 hover:text-black"}`}>
              <Icon size={14} /> {t.lbl}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview marketer={marketer} sales={sales} data={data} />}
      {tab === "leads" && <LeadsView marketerOnly={true} />}
      {tab === "reports" && <DailyReportsView submitOnly={true} />}
      {tab === "wallet" && <WalletTab marketer={marketer} commissions={commissions} payouts={payouts} />}
    </div>
  );
}

function Overview({ marketer, sales, data }) {
  return (
    <div data-testid="mp-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat title="Lifetime Earnings" value={ngn(marketer.lifetime_earnings)} icon={Award} accent="from-amber-500 to-orange-500" />
        <Stat title="Lifetime Paid" value={ngn(marketer.lifetime_paid)} icon={Wallet} accent="from-emerald-500 to-teal-500" />
        <Stat title="Total Sales" value={marketer.sales_count} icon={TrendingUp} accent="from-blue-500 to-indigo-500" />
        <Stat title="Properties" value={data.properties?.length || 0} icon={Building2} accent="from-violet-500 to-fuchsia-500" />
      </div>

      <Section title="Recent sales">
        {sales.length === 0 ? <Empty msg="No sales yet — share your referral code to earn." /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Code</Th><Th>Property</Th><Th>Customer</Th><Th right>Price</Th><Th>Stage</Th></tr>
            </thead>
            <tbody>
              {sales.slice(0, 10).map((s) => (
                <tr key={s.id} className="border-t border-black/5">
                  <Td className="font-mono text-xs">{s.code}</Td>
                  <Td>{s.property_title}</Td>
                  <Td>{s.customer_name}</Td>
                  <Td right className="font-bold">{ngn(s.sale_price)}</Td>
                  <Td><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold capitalize">{s.stage}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function WalletTab({ marketer, commissions, payouts }) {
  return (
    <div data-testid="mp-wallet">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Stat title="Current Balance" value={ngn(marketer.wallet_balance)} icon={Wallet} accent="from-emerald-500 to-teal-500" />
        <Stat title="Lifetime Earnings" value={ngn(marketer.lifetime_earnings)} icon={Award} accent="from-amber-500 to-orange-500" />
        <Stat title="Lifetime Paid" value={ngn(marketer.lifetime_paid)} icon={DollarSign} accent="from-blue-500 to-indigo-500" />
      </div>
      <Section title="Commissions">
        {commissions.length === 0 ? <Empty msg="No commissions yet" /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Property</Th><Th>Kind</Th><Th right>%</Th><Th right>Amount</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              {commissions.slice(0, 20).map((c) => (
                <tr key={c.id} className="border-t border-black/5">
                  <Td>{c.property_title}</Td>
                  <Td className="capitalize">{c.kind}</Td>
                  <Td right>{c.pct}%</Td>
                  <Td right className="font-bold text-emerald-700">{ngn(c.amount)}</Td>
                  <Td><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${c.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section title="Payout history">
        {payouts.length === 0 ? <Empty msg="No payout requests" /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Bank</Th><Th>Account</Th><Th right>Amount</Th><Th>Status</Th><Th>Requested</Th></tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`marketer-payout-${p.id}`}>
                  <Td>{p.bank_name}</Td>
                  <Td className="font-mono text-xs">{p.account_number}</Td>
                  <Td right className="font-bold">{ngn(p.amount)}</Td>
                  <Td><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : p.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></Td>
                  <Td className="text-xs text-black/55">{p.created_at?.slice(0, 10)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function Stat({ title, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-4 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold text-black/55 tracking-wider">{title}</div>
          <div className="mt-1 font-display font-extrabold text-2xl text-black tabular-nums">{value}</div>
        </div>
        {Icon && <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-sm bg-gradient-to-br ${accent || "from-zinc-400 to-zinc-600"}`}><Icon size={18} /></div>}
      </div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 mb-6 overflow-hidden">
      <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base text-black">{title}</div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
function Empty({ msg }) { return <div className="p-10 text-center text-sm text-black/45">{msg}</div>; }
function Th({ children, right }) { return <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
